import { Select } from '@chopchop-se/ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useArticleOptions,
  useSupplierOptions,
  type ArticleOption,
  type SupplierOption,
} from '../master-data-port';

/**
 * Register pickers for the two forms that cannot be driven by a scan: creating
 * a purchase order and teaching a barcode.
 *
 * They read M1 master-data through the local port (`master-data-port.ts`). When
 * that module is not deployed yet the picker says so in plain Swedish instead
 * of rendering an empty dropdown the operator cannot explain.
 */

export function ArticlePicker({
  value,
  onChange,
  label,
  error,
  disabled,
}: {
  value: string | null;
  onChange: (article: ArticleOption | null) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('receiving');
  const [search, setSearch] = useState('');
  const { options, unavailable, isLoading } = useArticleOptions(search, !disabled);

  return (
    <Select
      label={label ?? t('po.form.article')}
      placeholder={
        unavailable
          ? t('articlePicker.unavailable')
          : isLoading
            ? t('articlePicker.loading')
            : t('po.form.articlePlaceholder')
      }
      searchable
      searchPlaceholder={t('articlePicker.search')}
      options={options.map((article) => ({
        value: article.id,
        label: `${article.articleNo} · ${article.name}`,
      }))}
      value={value ?? ''}
      disabled={disabled || unavailable}
      {...(error ? { error } : {})}
      onChange={(event) => {
        const id = event.target.value;
        setSearch('');
        onChange(options.find((a) => a.id === id) ?? null);
      }}
    />
  );
}

export function SupplierPicker({
  value,
  onChange,
  label,
  error,
  disabled,
}: {
  value: string | null;
  onChange: (supplier: SupplierOption | null) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('receiving');
  const { options, unavailable, isLoading } = useSupplierOptions('', !disabled);

  return (
    <Select
      label={label ?? t('po.form.supplier')}
      placeholder={
        unavailable
          ? t('supplierPicker.unavailable')
          : isLoading
            ? t('articlePicker.loading')
            : t('po.form.supplierPlaceholder')
      }
      searchable
      searchPlaceholder={t('supplierPicker.search')}
      options={options.map((supplier) => ({ value: supplier.id, label: supplier.name }))}
      value={value ?? ''}
      disabled={disabled || unavailable}
      {...(error ? { error } : {})}
      onChange={(event) => {
        const id = event.target.value;
        onChange(options.find((s) => s.id === id) ?? null);
      }}
    />
  );
}
